import React, { FC, useReducer, useMemo, HTMLAttributes } from 'react';
import { makeStyles, withStyles } from 'tss-react/mui';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@mui/material';
import CircleCheckedFilled from '@mui/icons-material/CheckCircle';
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import AddIcon from '@mui/icons-material/Add';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { IncentiveAward, AwardOperatorType, IncentiveFieldType } from '../../../types/incentive-types';
import { numberSeparator, removeSeparator, useUpdateEffect } from 'src/app/common/utils';
import { NET_CASE_TEXT, NET_AFYP_TEXT, MANDATORY_FIELD_ERROR_TEXT, CHOOSE_ONE_ERROR_TEXT } from '../../../constants';
import { ErrorState } from './IncentiveDetailForm';

type MiniIncentiveAwardTableProps = {
  errorState: Pick<ErrorState['mandatory'], 'requirementEmpty' | 'awardFirstRowName' | 'awardFirstRowQuota'>;
  onDismissError: (field: keyof ErrorState['mandatory'], value: any) => void;
  isDisabled: boolean;
  incentiveAward?: IncentiveAward[];
  onUpdate: (incentiveAward: IncentiveAward[]) => void;
};

const useStyles = makeStyles()(() => ({
  rowContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  fieldContainer: {
    minWidth: 160,
    boxSizing: 'border-box'
  },
  field: {
    fontSize: '1rem',
    marginRight: 10,
  },
  mandatory: {
    color: 'red'
  },
  checkboxRowContainer: {
    display: 'flex'
  },
  inputBox: {
    paddingTop: 5,
    paddingBottom: 8
  },
  sectionMargin: {
    marginBottom: 15
  },
  divideMargin: {
    marginBottom: 10
  },
  subHeader: {
    fontSize: '1.1rem',
    fontWeight: 'bold'
  }
}));

const IncentiveTableCell = withStyles(TableCell, (theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: '1rem',
    paddingTop: 10,
    paddingBottom: 10
  },
  body: {
    fontWeight: 'bold',
    paddingTop: 8,
    paddingBottom: 8
  }
}));

type RequirementItem = {
  operand: string;
  operator: AwardOperatorType;
  type: IncentiveFieldType;
  values: string;
}

type TierAwardItem = {
  name: string;
  rank: string;
  quota: string;
  tier: number;
  requirements: RequirementItem[];
};

type TierAwardState = TierAwardItem[];

const initialRequirement: RequirementItem[] = [
  {
    operand: NET_CASE_TEXT,
    operator: AwardOperatorType.GREATER_THAN_EQUAL,
    type: IncentiveFieldType.NUMBER,
    values: ''
  }
];

const initialState: TierAwardState = [{
  name: "",
  rank: "",
  quota: "",
  tier: 1,
  requirements: initialRequirement
}];

type AddRowAction = {
  type: "ADD_TIER_AWARD_ROW";
  payload: {
    requirements: RequirementItem[];
  };
};

type ModifyRequirementAction = {
  type: "MODIFY_REQUIREMENT";
  payload: {
    requirements: RequirementItem[];
  };
};

type ModifyRowFieldAction = {
  type: "MODIFY_AWARD_ROW_FIELD";
  payload: {
    index: number;
    field: keyof Pick<TierAwardItem, 'name' | 'quota'>;
    value: string;
  };
};

type DeleteAwardRowAction = {
  type: "DELETE_AWARD_ROW";
  payload: {
    index: number;
  }
}

type TierAwardAction = AddRowAction | ModifyRequirementAction | ModifyRowFieldAction | DeleteAwardRowAction;

const awardReducer = (state: TierAwardState, action: TierAwardAction): TierAwardState => {
  switch (action.type) {
    case "ADD_TIER_AWARD_ROW":
      return [
        ...state,
        {
          name: "",
          rank: "",
          quota: "",
          tier: state.length + 1,
          requirements: action.payload.requirements
        }
      ];
    case "MODIFY_REQUIREMENT":
      return state.map(item => ({
        ...item,
        requirements: action.payload.requirements
      }));
    case "MODIFY_AWARD_ROW_FIELD":
      let newState = [...state];
      let item = newState[action.payload.index];
      item[action.payload.field] = action.payload.value;
      return newState.map((item, index) => {
        let count = 1;
        const currentQuota = Number(removeSeparator(item.quota));
        for (let i = 0; i < index; i++) {
          let value = newState[i].quota;
          count += Number(removeSeparator(value));
        };
        return {
          ...item,
          rank: count && currentQuota ? `${count} - ${count + currentQuota - 1}` : ''
        };
      });
    case "DELETE_AWARD_ROW":
      let newArr = [...state];
      newArr.splice(action.payload.index, 1);
      return [...newArr];
    default:
      return [...state];
  }
};

const awardToStateConvertor = (incentiveAward?: IncentiveAward[]): TierAwardState => {
  if (incentiveAward && incentiveAward.length > 0) {
    return incentiveAward.map((award, index) => {
      let name = award.properties.find(property => property.key === 'name');
      let quota = award.properties.find(property => property.key === 'quota');

      //calculate rank
      let count = 1;
      const currentQuota = quota ? Number(removeSeparator(quota.value)) : undefined;
      for (let i = 0; i < index; i++) {
        let lastProperties = incentiveAward[i].properties;
        let lastQuota = lastProperties.find(property => property.key === 'quota');
        if (lastQuota) {
          count += Number(removeSeparator(lastQuota.value));
        }
      };
      const rank = count && currentQuota ? `${count} - ${count + currentQuota - 1}` : '';

      let tier = award.properties.find(property => property.key === 'tier');
      let requirements = award.requirements.map((requirement, index) => ({
        operand: requirement.operand,
        operator: requirement.operator,
        type: requirement.type,
        values: requirement.values[0] ? String(numberSeparator(String(requirement.values[0]))) : ""
      }));
      return {
        name: name ? name.value : '',
        rank,
        quota: quota ? quota.value : '',
        tier: tier ? Number(tier.value) : 1,
        requirements
      };
    });
  } else {
    return initialState;
  }
};

const MiniIncentiveAwardTable: FC<MiniIncentiveAwardTableProps & HTMLAttributes<HTMLDivElement>> = ({
  errorState,
  onDismissError,
  isDisabled,
  incentiveAward,
  onUpdate,
  ...rest
}) => {
  const { classes } = useStyles();
  const [awardState, awardDispatch] = useReducer(awardReducer, awardToStateConvertor(incentiveAward));

  const { requirements, netCase, netAfyp } = useMemo(() => {
    let requirements: RequirementItem[] = [];
    let netCase: RequirementItem | undefined = undefined;
    let netAfyp: RequirementItem | undefined = undefined;
    if (awardState[0] && awardState[0].requirements) {
      requirements = awardState[0].requirements;
      netCase = requirements.find(requirement => requirement.operand === NET_CASE_TEXT);
      netAfyp = requirements.find(requirement => requirement.operand === NET_AFYP_TEXT);
    }
    return {
      requirements,
      netCase,
      netAfyp
    };
  }, [awardState]);

  useUpdateEffect(() => {
    onUpdate(awardState.map(tierAward => ({
      requirements: requirements.map(requirement => ({
        operand: requirement.operand,
        operator: requirement.operator,
        type: requirement.type,
        values: [Number(removeSeparator(requirement.values))]
      })),
      properties: [
        {
          key: "name",
          value: tierAward.name
        },
        {
          key: "tier",
          value: String(tierAward.tier)
        },
        {
          key: "quota",
          value: removeSeparator(tierAward.quota)
        }
      ]
    })));
  }, [awardState]);

  return (
    <div {...rest}>
      <div style={{ marginBottom: 5 }} className={classes.checkboxRowContainer}>
        <div
          style={{ paddingTop: 9 }}
          className={classes.fieldContainer}
        >
          <span className={classes.field}>Net Case Target :</span>
        </div>
        <FormControlLabel
          control={
            <Checkbox
              disabled={isDisabled}
              icon={<CircleUnchecked />}
              checkedIcon={<CircleCheckedFilled />}
              checked={!!!netCase}
              onChange={e => {
                if (e.target.checked) {
                  let newArr = [...requirements];
                  const foundIndex = requirements.findIndex(requirement => requirement.operand === NET_CASE_TEXT);
                  if (foundIndex !== -1) {
                    newArr.splice(foundIndex, 1);
                    awardDispatch({ type: "MODIFY_REQUIREMENT", payload: { requirements: newArr } });
                  }
                }
              }}
            />
          }
          label="No Requirement"
          labelPlacement="end"
        />
        <FormControlLabel
          control={
            <Checkbox
              disabled={isDisabled}
              icon={<CircleUnchecked />}
              checkedIcon={<CircleCheckedFilled />}
              checked={!!netCase}
              onChange={e => {
                if (e.target.checked) {
                  let newArr = [...requirements];
                  const foundIndex = requirements.findIndex(requirement => requirement.operand === NET_CASE_TEXT);
                  if (foundIndex === -1) {
                    newArr.push({
                      operand: NET_CASE_TEXT,
                      operator: AwardOperatorType.GREATER_THAN_EQUAL,
                      type: IncentiveFieldType.NUMBER,
                      values: ''
                    });
                    awardDispatch({ type: "MODIFY_REQUIREMENT", payload: { requirements: newArr } });
                  }
                }
              }}
            />
          }
          label="Minimum"
          labelPlacement="end"
        />
        <TextField
          error={errorState.requirementEmpty}
          disabled={isDisabled || !!!netCase}
          style={{ margin: '0 0 0 10px', width: 150 }}
          margin="dense"
          variant="outlined"
          helperText={errorState.requirementEmpty && CHOOSE_ONE_ERROR_TEXT}
          value={netCase ? netCase.values : ""}
          onChange={e => {
            const foundIndex = requirements.findIndex(requirement => requirement.operand === NET_CASE_TEXT);
            if (foundIndex !== -1) {
              let newArr = [...requirements];
              const onlyNums = e.target.value.replace(/[^0-9]/g, '');
              newArr[foundIndex].values = numberSeparator(onlyNums);
              onDismissError('requirementEmpty', onlyNums);
              awardDispatch({ type: "MODIFY_REQUIREMENT", payload: { requirements: newArr } })
            }
          }}
        />
      </div>
      <div style={{ marginBottom: 10 }} className={classes.checkboxRowContainer}>
        <div
          style={{ paddingTop: 9 }}
          className={classes.fieldContainer}
        >
          <span className={classes.field}>Total AFYP Target :</span>
        </div>
        <FormControlLabel
          control={
            <Checkbox
              disabled={isDisabled}
              icon={<CircleUnchecked />}
              checkedIcon={<CircleCheckedFilled />}
              checked={!!!netAfyp}
              onChange={e => {
                if (e.target.checked) {
                  let newArr = [...requirements];
                  const foundIndex = requirements.findIndex(requirement => requirement.operand === NET_AFYP_TEXT);
                  if (foundIndex !== -1) {
                    newArr.splice(foundIndex, 1);
                    awardDispatch({ type: "MODIFY_REQUIREMENT", payload: { requirements: newArr } });
                  }
                }
              }}
            />
          }
          label="No Requirement"
          labelPlacement="end"
        />
        <FormControlLabel
          control={
            <Checkbox
              disabled={isDisabled}
              icon={<CircleUnchecked />}
              checkedIcon={<CircleCheckedFilled />}
              checked={!!netAfyp}
              onChange={e => {
                if (e.target.checked) {
                  let newArr = [...requirements];
                  const foundIndex = requirements.findIndex(requirement => requirement.operand === NET_AFYP_TEXT);
                  if (foundIndex === -1) {
                    newArr.push({
                      operand: NET_AFYP_TEXT,
                      operator: AwardOperatorType.GREATER_THAN_EQUAL,
                      type: IncentiveFieldType.NUMBER,
                      values: ''
                    });
                    awardDispatch({ type: "MODIFY_REQUIREMENT", payload: { requirements: newArr } });
                  }
                }
              }}
            />
          }
          label="Minimum"
          labelPlacement="end"
        />
        <TextField
          error={errorState.requirementEmpty}
          disabled={isDisabled || !!!netAfyp}
          style={{ margin: '0 0 0 10px', width: 150 }}
          margin="dense"
          variant="outlined"
          helperText={errorState.requirementEmpty && CHOOSE_ONE_ERROR_TEXT}
          value={netAfyp ? netAfyp.values : ""}
          onChange={e => {
            const foundIndex = requirements.findIndex(requirement => requirement.operand === NET_AFYP_TEXT);
            if (foundIndex !== -1) {
              let newArr = [...requirements];
              const onlyNums = e.target.value.replace(/[^0-9]/g, '');
              newArr[foundIndex].values = numberSeparator(onlyNums);
              onDismissError('requirementEmpty', onlyNums);
              awardDispatch({ type: "MODIFY_REQUIREMENT", payload: { requirements: newArr } })
            }
          }}
        />
      </div>
      <div className={classes.checkboxRowContainer}>
        <div
          style={{ paddingTop: 14 }}
          className={classes.fieldContainer}
        >
          <span className={classes.field}>Award Settings :</span>
        </div>
        <div style={{ flexGrow: 1 }}>
          <TableContainer component={Paper} style={{ tableLayout: 'fixed' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <IncentiveTableCell>Tier</IncentiveTableCell>
                  <IncentiveTableCell>Rank</IncentiveTableCell>
                  <IncentiveTableCell>Quota</IncentiveTableCell>
                  <IncentiveTableCell>Name</IncentiveTableCell>
                  {requirements.map((requirementItem, index) => (
                    <IncentiveTableCell
                      key={`header-cell-${index}`}
                      align="center"
                    >
                      {`${requirementItem.operand} (${requirementItem.operator})`}
                    </IncentiveTableCell>
                  ))}
                  <IncentiveTableCell align="right">
                    <IconButton
                      disabled={isDisabled}
                      style={{ padding: 5, color: 'white' }}
                      onClick={() => awardDispatch({ type: "ADD_TIER_AWARD_ROW", payload: { requirements } })}
                    >
                      <AddIcon />
                    </IconButton>
                  </IncentiveTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {awardState.map((awardItem, awardIndex) => (
                  <TableRow key={`award-item-${awardIndex}`}>
                    <IncentiveTableCell>{awardItem.tier}</IncentiveTableCell>
                    <IncentiveTableCell>{awardItem.rank}</IncentiveTableCell>
                    <IncentiveTableCell>
                      <TextField
                        error={awardIndex === 0 && errorState.awardFirstRowQuota}
                        disabled={isDisabled}
                        style={{ margin: 0 }}
                        InputProps={{
                          classes: {
                            input: classes.inputBox,
                          },
                        }}
                        margin="dense"
                        variant="outlined"
                        helperText={awardIndex === 0 && errorState.awardFirstRowQuota && MANDATORY_FIELD_ERROR_TEXT}
                        value={awardItem.quota}
                        onChange={e => {
                          const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                          if (awardIndex === 0) {
                            onDismissError("awardFirstRowQuota", onlyNums);
                          }
                          awardDispatch({
                            type: "MODIFY_AWARD_ROW_FIELD",
                            payload: {
                              index: awardIndex,
                              field: "quota",
                              value: numberSeparator(onlyNums)
                            }
                          })
                        }}
                      />
                    </IncentiveTableCell>
                    <IncentiveTableCell style={{ borderRight: '1px solid rgba(224, 224, 224, 1)'}}>
                      <TextField
                        error={awardIndex === 0 && errorState.awardFirstRowName}
                        disabled={isDisabled}
                        style={{ margin: 0 }}
                        InputProps={{
                          classes: {
                            input: classes.inputBox,
                          },
                        }}
                        margin="dense"
                        variant="outlined"
                        helperText={awardIndex === 0 && errorState.awardFirstRowName && MANDATORY_FIELD_ERROR_TEXT}
                        value={awardItem.name}
                        onChange={e => {
                          if (awardIndex === 0) {
                            onDismissError("awardFirstRowName", e.target.value);
                          }
                          awardDispatch({
                            type: "MODIFY_AWARD_ROW_FIELD",
                            payload: {
                              index: awardIndex,
                              field: "name",
                              value: e.target.value
                            }
                          })
                        }}
                      />
                    </IncentiveTableCell>
                    {awardIndex === 0 && requirements.map((item, index) => {
                      const requirementItem = requirements.find(requirement => requirement.operand === item.operand);
                      return requirementItem && (
                        <IncentiveTableCell
                          key={`requirement-item-children-${index}`}
                          rowSpan={awardState.length}
                          align="center"
                        >
                          {requirementItem.values || '0'}
                        </IncentiveTableCell>
                      )
                    })}
                    <IncentiveTableCell
                      style={{
                        borderLeft: '1px solid rgba(224, 224, 224, 1)'
                      }}
                      align="right"
                    >
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() => awardDispatch({ type: 'DELETE_AWARD_ROW', payload: { index: awardIndex } })}
                        disabled={isDisabled || awardIndex === 0}
                      >
                        <HighlightOffIcon />
                      </IconButton>
                    </IncentiveTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default MiniIncentiveAwardTable;
